/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/WikiPage.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                      |
 * |---|------------------------|-------------------|------------------|-----------------------------------|
 * | 1 | 2026-06-21             | Claude Sonnet 4.6 | Item 3 of 7-plan | Created — comprehensive in-app wiki|
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<WikiPage />)
 *
 * EXPORTS
 * -------
 * WikiPage — full in-app documentation, searchable, organized by category
 *
 * =============================================================================
 */

import { useState, useMemo } from "react";

// ── Content types ─────────────────────────────────────────────────────────────

type Block =
  | { t: "p"; s: string }
  | { t: "h3"; s: string }
  | { t: "h4"; s: string }
  | { t: "code"; lang: string; s: string }
  | { t: "tip"; s: string }
  | { t: "warn"; s: string }
  | { t: "info"; s: string }
  | { t: "steps"; items: string[] }
  | { t: "list"; items: string[] }
  | { t: "table"; headers: string[]; rows: string[][] };

interface Article {
  id: string;
  title: string;
  tags: string[];      // for search
  blocks: Block[];
}

interface Category {
  id: string;
  label: string;
  icon: string;
  articles: Article[];
}

// ── Wiki content ──────────────────────────────────────────────────────────────

const WIKI: Category[] = [
  // ── GETTING STARTED ──────────────────────────────────────────────────────
  {
    id: "start", label: "Getting Started", icon: "🚀",
    articles: [
      {
        id: "connect", title: "Connecting your Android phone", tags: ["connect","usb","adb","developer"],
        blocks: [
          { t: "p", s: "Mimiq uses WebUSB to communicate with your phone directly from Chrome — no desktop app, no ADB server needed." },
          { t: "h3", s: "Requirements" },
          { t: "list", items: [
            "Chrome or Edge browser (not Firefox or Safari — WebUSB is Chromium-only)",
            "Android phone with USB cable (data cable, not charge-only)",
            "USB Debugging enabled on the phone",
          ]},
          { t: "h3", s: "Step-by-step" },
          { t: "steps", items: [
            "Enable Developer Options: go to Settings → About Phone → tap Build Number 7 times until you see 'You are now a developer!'",
            "Enable USB Debugging: Settings → Developer Options → USB Debugging → toggle ON",
            "Connect your phone to the PC with a USB cable",
            "Open Mimiq Studio in Chrome and click Connect Phone (top-right)",
            "A browser dialog lists USB devices — select your phone and click Connect",
            "A dialog appears on your phone: 'Allow USB debugging?' — tap Allow (check Always allow to avoid repeating this)",
            "The phone mirror should appear within a few seconds",
          ]},
          { t: "warn", s: "If your phone does not appear in the Chrome dialog: try a different USB port, try a different cable, restart Chrome, or run adb kill-server in a terminal to release any existing ADB connection." },
          { t: "h3", s: "Samsung extra steps" },
          { t: "list", items: [
            "Build Number is at Settings → Software Information → Build Number",
            "Also enable Install via USB and USB Debugging (Security Settings)",
          ]},
          { t: "h3", s: "Xiaomi extra steps" },
          { t: "list", items: [
            "Enable USB Debugging (Security Settings) in Developer Options",
            "Enable MI Unlock Status if required",
          ]},
        ],
      },
      {
        id: "first-recording", title: "Your first recording (5-minute walkthrough)", tags: ["record","start","walkthrough"],
        blocks: [
          { t: "steps", items: [
            "Connect your phone (see Connecting your Android phone)",
            "Open the app you want to test on the phone",
            "In Mimiq Studio, click ▶ Start Recording (the big green button on the Record tab)",
            "Tap elements on the phone mirror — each tap creates a step in the right panel",
            "Use the Custom Commands toolbar to add Wait, Type Text, Assert, etc.",
            "Click Stop Recording when done",
            "Click Save As to name your test and choose a folder (suite)",
            "Click Run on the Execution tab to replay — watch the phone execute each step automatically",
          ]},
          { t: "tip", s: "Your first test does not need to be perfect. Record a happy path, then go back and add assertions (Verify Visible) after key screens to make it robust." },
        ],
      },
    ],
  },

  // ── RECORD MODE ──────────────────────────────────────────────────────────
  {
    id: "record", label: "Recording", icon: "⏺",
    articles: [
      {
        id: "drive-mode", title: "Drive Mode — recording taps", tags: ["drive","record","tap","mode"],
        blocks: [
          { t: "p", s: "Drive Mode is the default mode when you are connected and recording. Every tap you make on the phone mirror is forwarded to the real phone AND recorded as an IR step." },
          { t: "h3", s: "How to enter Drive Mode" },
          { t: "list", items: [
            "Click the ⏺ Drive button (top of the right panel) — it turns green when active",
            "Drive Mode is always active by default after connecting",
          ]},
          { t: "h3", s: "What Drive Mode records" },
          { t: "table",
            headers: ["Action", "Recorded as"],
            rows: [
              ["Single tap", "tap step — with the element's locators"],
              ["Tap while recording off", "Tap is forwarded to phone only — nothing recorded"],
              ["Swipe on canvas", "swipe step with from/to coordinates"],
              ["Long press (hold >500ms)", "longPress step"],
            ],
          },
          { t: "tip", s: "Drive Mode works even when you are NOT recording — you can operate the phone through the mirror without creating steps. This is useful for navigating to the right screen before starting a recording." },
        ],
      },
      {
        id: "inspect-mode", title: "Inspect Mode — viewing element attributes", tags: ["inspect","attribute","locator","element","xpath"],
        blocks: [
          { t: "p", s: "Inspect Mode lets you click any element on the phone mirror to see its accessibility attributes — resource-id, text, content-desc, class, bounds, and all boolean states." },
          { t: "h3", s: "Activating Inspect Mode" },
          { t: "list", items: [
            "Click the 🔍 Inspect button in the recorder header — it turns cyan when active",
            "A cyan banner appears below the buttons confirming you are in Inspect Mode",
          ]},
          { t: "h3", s: "Using Inspect Mode" },
          { t: "steps", items: [
            "Click the 🔍 Inspect button to switch to Inspect Mode",
            "Click any element on the phone mirror",
            "A tree dump runs (takes 1–3 seconds on first tap)",
            "A cyan highlight rect appears over the matched element",
            "The right panel shows all attributes: text, resourceId, contentDesc, className, enabled, checked, bounds, etc.",
            "Click 'Add assertion' to quickly add a Verify Visible step for that element",
            "Click the ⏺ Drive button to switch back to recording",
          ]},
          { t: "h3", s: "Example — finding a button's resource-id" },
          { t: "steps", items: [
            "Switch to Inspect Mode",
            "Click the Login button on the mirror",
            "The panel shows: resourceId = com.example.app:id/btn_login, text = Login, enabled = true, clickable = true",
            "Copy the resourceId to use as an a11yId locator — this is the most stable selector",
          ]},
          { t: "tip", s: "Inspect Mode is your best tool for finding element locators before setting up assertions or Get Attribute steps. It shows EXACTLY what the accessibility tree sees." },
          { t: "warn", s: "Inspect Mode does NOT forward taps to the phone. Clicks only inspect — they do not interact with the app. Switch back to Drive Mode to interact." },
        ],
      },
      {
        id: "rerecord-step", title: "Re-recording a single step", tags: ["rerecord","re-record","fix","step"],
        blocks: [
          { t: "p", s: "If a specific recorded step is wrong, you can re-record just that step without losing the rest of the test." },
          { t: "steps", items: [
            "In the step list (right panel), find the step you want to fix",
            "Click the ↺ icon on that step row",
            "Mimiq enters Re-record mode — a banner appears: 'Re-recording step N'",
            "Tap the correct element on the phone mirror",
            "The step is automatically replaced with the new tap",
            "Re-record mode exits automatically after one tap",
          ]},
          { t: "tip", s: "Re-recording only replaces the tap target (selector). The step type and other properties stay the same. To change the step type, delete it and add a new step." },
        ],
      },
      {
        id: "add-step-manual", title: "Adding a step manually (without recording)", tags: ["add","step","manual","custom"],
        blocks: [
          { t: "p", s: "You can add steps at any time without recording from the phone. Use the + Add Step button at the bottom of the step list." },
          { t: "steps", items: [
            "Scroll to the bottom of the step list in the right panel",
            "Click + Add Step",
            "Type a command name (e.g. 'wait', 'tap', 'assert', 'screenshot')",
            "A dropdown filters as you type — select the command",
            "Fill in the required fields (element locator, duration, message, etc.)",
            "Click Add Step to insert it at the end of the test",
          ]},
          { t: "h3", s: "Searching for commands" },
          { t: "p", s: "The + Add Step search understands all command names and descriptions:" },
          { t: "list", items: [
            "Type 'dark' → finds Dark Mode On / Dark Mode Off",
            "Type 'compare' → finds Compare Values",
            "Type 'attr' → finds Get Attribute",
            "Type 'hold' → finds Hold & Scroll",
            "Type 'wifi' → finds WiFi On / WiFi Off",
            "Type 'assert' → finds Assert Visible / Hidden / Enabled",
          ]},
          { t: "tip", s: "The + Add Step command always inserts at the END of the step list. To insert in the middle, add at the end then drag the step to the correct position." },
        ],
      },
      {
        id: "edit-recorded-step", title: "Editing a recorded step", tags: ["edit","step","modify","change"],
        blocks: [
          { t: "p", s: "Every step in the list has an edit (pencil) icon. Clicking it opens the step editor inline." },
          { t: "h3", s: "What you can edit" },
          { t: "table",
            headers: ["Step type", "Editable fields"],
            rows: [
              ["tap", "Locator candidates (add/remove/reorder), description"],
              ["wait", "Duration in milliseconds"],
              ["typeText", "Text to type"],
              ["launchApp / stopApp", "Package name"],
              ["assert", "Condition (visible/hidden/enabled/disabled/hasText) + expected text"],
              ["if", "Element/value condition, then/else branches"],
              ["repeat", "Repeat count"],
              ["evaluate", "Left, operator, right, storeAs, options (trim/ignoreCase)"],
              ["setVariable", "Variable name, source (literal/capture/random/attribute)"],
              ["screenshot", "Optional label"],
              ["swipe", "Duration (speed of swipe)"],
            ],
          },
          { t: "h3", s: "Steps to edit" },
          { t: "steps", items: [
            "Find the step in the step list",
            "Click the pencil ✎ icon on the right side of the row",
            "The step expands inline with editable fields",
            "Make your changes",
            "Click Save — the step updates immediately",
            "Click Cancel to discard changes",
          ]},
        ],
      },
      {
        id: "copy-test", title: "Copying and organizing test cases", tags: ["copy","duplicate","suite","folder","organize"],
        blocks: [
          { t: "p", s: "The Test Cases tab is your test library. Tests are organized in Suites (folders) and can be copied, moved, renamed, or deleted." },
          { t: "h3", s: "Create a suite (folder)" },
          { t: "steps", items: [
            "Go to the Test Cases tab",
            "Click + New Suite",
            "Enter a name (e.g. Login Tests, Checkout, Regression)",
            "Click Create",
          ]},
          { t: "h3", s: "Copy / duplicate a test" },
          { t: "steps", items: [
            "In the Test Cases tab, hover over a test row",
            "Click the ⋮ (more actions) menu",
            "Select Duplicate",
            "A copy named 'Test Name (copy)' is created in the same suite",
          ]},
          { t: "h3", s: "Move a test to a different suite" },
          { t: "steps", items: [
            "Click the ⋮ menu on the test row",
            "Select Move to Suite",
            "Pick the destination suite",
            "Confirm",
          ]},
          { t: "h3", s: "Export a test" },
          { t: "steps", items: [
            "Check one or more tests in the Test Cases tab",
            "Click Download Test Case (single) or Export as ZIP (multiple)",
            "Share the .json or .zip file with teammates",
          ]},
        ],
      },
    ],
  },

  // ── LOCATORS ─────────────────────────────────────────────────────────────
  {
    id: "locators", label: "Locators & Selectors", icon: "🔍",
    articles: [
      {
        id: "locator-overview", title: "Understanding element locators", tags: ["locator","selector","xpath","a11yid","text","contentdesc"],
        blocks: [
          { t: "p", s: "When Mimiq records a tap, it finds the element in the accessibility tree and stores multiple ways to identify it — these are called candidates. At replay time, candidates are tried in order until one matches." },
          { t: "h3", s: "Locator strategies (best to worst stability)" },
          { t: "table",
            headers: ["Strategy", "Matches on", "Stability", "Use when"],
            rows: [
              ["a11yId", "android:resource-id (e.g. com.app:id/login_btn)", "🟢 High", "Element has a unique resource-id (developer set this)"],
              ["text", "Exact visible text (e.g. 'Login')", "🟢 High", "Button/label has stable, unique text"],
              ["contentDesc", "android:contentDescription (accessibility label)", "🟢 High", "Icon buttons / image buttons with content-desc"],
              ["textContains", "Partial text match (e.g. contains 'Login')", "🟡 Medium", "Text varies slightly across versions"],
              ["textRegex", "Regex pattern match on text", "🟡 Medium", "Dynamic text (e.g. dates, counts)"],
              ["relative", "Spatial: below(a11yId='x')", "🟡 Medium", "Element near a stable anchor"],
              ["xpath", "XPath expression on accessibility tree", "🔴 Fragile", "Last resort — position-based"],
              ["className", "Android widget class name", "🔴 Fragile", "Almost never — too generic"],
            ],
          },
          { t: "tip", s: "Always prefer a11yId > text > contentDesc. Use Inspect Mode to find the resource-id before recording taps." },
        ],
      },
      {
        id: "locator-colors", title: "Locator color codes", tags: ["color","green","yellow","red","confidence","quality"],
        blocks: [
          { t: "p", s: "Each candidate in the step editor is color-coded by reliability:" },
          { t: "table",
            headers: ["Color", "Meaning", "Strategies"],
            rows: [
              ["🟢 Green", "High confidence — very likely to find the right element", "a11yId, text, contentDesc"],
              ["🟡 Yellow", "Medium confidence — may fail if the UI changes structure", "textContains, textRegex, relative"],
              ["🔴 Red", "Fragile — position-based, breaks easily with UI changes", "xpath (positional), className"],
              ["⚫ Gray", "No confidence assessment (platform-specific)", "iosPredicate, iosClassChain, css"],
            ],
          },
          { t: "p", s: "Mimiq records MULTIPLE candidates per tap and tries them in order. Even if a green candidate fails, a yellow fallback may still work. The best test has at least one green candidate per tap step." },
          { t: "warn", s: "If all your candidates are red/gray, your test is fragile. Use Inspect Mode to find a better locator and add it as a new candidate." },
        ],
      },
      {
        id: "try-first", title: "Try First button — promoting a better locator", tags: ["try first","promote","reorder","candidate"],
        blocks: [
          { t: "p", s: "Each tap step stores multiple candidate locators. At replay time, Mimiq tries them in order (top to bottom). The Try First button moves a candidate to position #1 so it is attempted before all others." },
          { t: "h3", s: "When to use Try First" },
          { t: "list", items: [
            "A replay fails because the first candidate (e.g. xpath) does not match, but a lower candidate (e.g. text) would",
            "You manually added a better locator and want it to take priority",
            "The AI healing suggested a better candidate — you want to commit it as primary",
          ]},
          { t: "steps", items: [
            "Expand a tap step by clicking the chevron ▸ on the left",
            "The candidate list shows all stored locators with their strategies",
            "Find the candidate you want to make primary",
            "Click the ↑ Try first button on that candidate row",
            "The candidate moves to position #1",
            "Save the test — the change persists immediately",
          ]},
          { t: "tip", s: "After Try First, run a replay to confirm the promoted candidate actually matches. If it fails, the next candidate in the list is tried automatically." },
        ],
      },
      {
        id: "add-candidate", title: "Adding a new element locator manually", tags: ["add","locator","candidate","manual","xpath"],
        blocks: [
          { t: "p", s: "You can add additional locator candidates to any tap step at any time." },
          { t: "steps", items: [
            "Expand a tap step in the step list",
            "Click + Add candidate at the bottom of the candidate list",
            "Select the locator strategy from the dropdown (a11yId, text, xpath, etc.)",
            "Enter the value (e.g. the resource-id string, or the text label)",
            "Optionally set an Index (0-based sibling index) if multiple elements match",
            "Click Add",
          ]},
          { t: "h3", s: "Index field (0-based)" },
          { t: "p", s: "If a locator value matches multiple elements, the Index field selects which one:" },
          { t: "code", lang: "text", s: "Index 0 = first matching element\nIndex 1 = second matching element\n(and so on)" },
          { t: "warn", s: "Never rely on Index for fragile xpaths. Index is safest with a11yId or text where the position is semantically meaningful (e.g. 'list item #2 by text LoginUser')." },
        ],
      },
    ],
  },

  // ── CUSTOM COMMANDS ───────────────────────────────────────────────────────
  {
    id: "commands", label: "Custom Commands", icon: "🔧",
    articles: [
      {
        id: "cmd-overview", title: "Custom Commands overview", tags: ["commands","toolbar","overview"],
        blocks: [
          { t: "p", s: "Custom Commands are step types you can add without recording a tap. They live in the CUSTOM COMMANDS toolbar (chip strip below the Drive/Inspect buttons). Click any category chip to expand its commands, then click a command to add it." },
          { t: "h3", s: "Categories at a glance" },
          { t: "table",
            headers: ["Category", "Commands"],
            rows: [
              ["App", "Open App, Close App"],
              ["Keys", "Home, Back, Enter, Tab, Delete"],
              ["Util", "Wait, Screenshot, Type Text"],
              ["Scroll", "Swipe Up, Swipe Down, Swipe Left, Swipe Right"],
              ["Touch", "Double Tap, Long Press"],
              ["Assert", "Verify Visible, Verify Hidden, Verify Enabled, Compare Values"],
              ["Input", "Clear Text, Hide Keyboard"],
              ["Wait", "Wait For Element"],
              ["Swipe", "Swipe Custom, Scroll To Element"],
              ["Variables", "Set Variable, Get Variable, Random Value, Get Attribute"],
              ["Gesture", "Hold & Scroll"],
              ["Report", "Print Log, ✓ Mark Pass, ✗ Mark Fail"],
              ["Control", "Stop Test"],
              ["Navigate", "Open URL, Deep Link"],
              ["Device", "WiFi On/Off, Airplane On/Off, Dark Mode On/Off, Location On/Off, Show Keyboard"],
              ["Flow", "If Condition, Repeat, For Each Row, Break, Continue, API Call, Script"],
            ],
          },
        ],
      },
      {
        id: "cmd-app", title: "App — Open App & Close App", tags: ["open app","close app","launch","package","apk"],
        blocks: [
          { t: "h3", s: "Open App" },
          { t: "p", s: "Launches an Android app by its package name. Optionally clears app data first (useful for clean-state tests)." },
          { t: "code", lang: "text", s: "Package: com.example.myapp\nClear state: OFF (default) — keeps user data\nClear state: ON — resets the app to factory state (slower)" },
          { t: "p", s: "To find a package name: connect your phone, go to the app, use Inspect Mode to see the 'pkg' field of any element." },
          { t: "h3", s: "Close App" },
          { t: "p", s: "Force-stops the app. Equivalent to swiping the app away in the recents screen. Use at the start/end of a test for isolation." },
          { t: "code", lang: "text", s: "Package: com.example.myapp" },
          { t: "tip", s: "Best practice: always start tests with Open App (with Clear State) and end with Close App for reliable, isolated test runs." },
        ],
      },
      {
        id: "cmd-keys", title: "Keys — Home, Back, Enter, Tab, Delete", tags: ["home","back","enter","tab","delete","key"],
        blocks: [
          { t: "table",
            headers: ["Command", "ADB keycode", "Use case"],
            rows: [
              ["Home", "KEYCODE_HOME", "Return to launcher screen"],
              ["Back", "KEYCODE_BACK", "Navigate back one screen / close dialogs"],
              ["Enter", "KEYCODE_ENTER", "Submit a form, confirm a selection"],
              ["Tab", "KEYCODE_TAB", "Move focus to the next form field"],
              ["Delete", "KEYCODE_DEL", "Backspace — delete one character"],
            ],
          },
          { t: "tip", s: "Use Back to dismiss dialogs, permission prompts, or return from a screen. Use Enter after Type Text to submit a search field." },
        ],
      },
      {
        id: "cmd-util", title: "Util — Wait, Screenshot, Type Text", tags: ["wait","screenshot","type text","input","delay"],
        blocks: [
          { t: "h3", s: "Wait" },
          { t: "p", s: "Pauses execution for a fixed number of milliseconds. Use for animations, page loads, or network calls with known latency." },
          { t: "code", lang: "text", s: "Duration: 2000   — wait 2 seconds\nDuration: 500    — wait 0.5 seconds" },
          { t: "warn", s: "Prefer Wait For Element over Wait when possible. Wait For Element stops as soon as the element appears (smarter). Fixed Wait can make tests slow or flaky if the load time varies." },
          { t: "h3", s: "Screenshot" },
          { t: "p", s: "Takes a screenshot of the device screen and attaches it to the run report. Appears in the Reports detail table and exported HTML." },
          { t: "code", lang: "text", s: "Label: checkout-complete   — optional label shown in the report" },
          { t: "warn", s: "Each screenshot is 1–5 MB (PNG at full phone resolution). Avoid taking screenshots on every step — use them sparingly at key checkpoints. See Settings → Screenshot Every Step for automatic capture." },
          { t: "h3", s: "Type Text" },
          { t: "p", s: "Types text into the currently focused field. The text field must be focused (tapped open) BEFORE this step runs." },
          { t: "code", lang: "text", s: "Text: hello@example.com\nText: My search query\nText: 42" },
          { t: "warn", s: "Type Text requires the field to already be focused. Add a tap step on the field first, then Type Text. For username/password fields, record the tap on the field, then add Type Text immediately after." },
        ],
      },
      {
        id: "cmd-scroll", title: "Scroll — Swipe Up/Down/Left/Right", tags: ["scroll","swipe","up","down","left","right"],
        blocks: [
          { t: "p", s: "The four scroll shortcuts perform fixed full-screen swipe gestures. They are the fastest way to scroll content." },
          { t: "table",
            headers: ["Command", "Finger movement", "Content moves"],
            rows: [
              ["Swipe Up", "Bottom → Top", "Downward (scroll down page)"],
              ["Swipe Down", "Top → Bottom", "Upward (scroll up / pull-to-refresh)"],
              ["Swipe Left", "Right → Left", "Rightward (go to next card/page)"],
              ["Swipe Right", "Left → Right", "Leftward (go to previous card/page)"],
            ],
          },
          { t: "code", lang: "text", s: "// Scroll down a list by swiping up repeatedly\nSwipe Up\nSwipe Up\nSwipe Up\n\n// Pull to refresh\nSwipe Down" },
          { t: "tip", s: "For precise control over start/end coordinates and duration, use Swipe Custom (in the Swipe category) instead." },
        ],
      },
      {
        id: "cmd-assert", title: "Assert — Verify Visible, Hidden, Enabled, Compare Values", tags: ["assert","verify","visible","hidden","enabled","check"],
        blocks: [
          { t: "h3", s: "Verify Visible" },
          { t: "p", s: "Asserts that an element is present and visible on screen. Fails the test if the element is not found." },
          { t: "code", lang: "text", s: "Element: Login button (by text or resource-id)\n→ Test fails if 'Login' is not on screen" },
          { t: "h3", s: "Verify Hidden" },
          { t: "p", s: "Asserts that an element is NOT on screen. Useful after dismissing a dialog or logging out." },
          { t: "code", lang: "text", s: "Element: Loading spinner (by content-desc='Loading')\n→ Test fails if spinner is still visible after network call" },
          { t: "h3", s: "Verify Enabled" },
          { t: "p", s: "Asserts that an element is interactive (enabled=true). Useful to verify that a Submit button becomes clickable after filling a form." },
          { t: "h3", s: "Compare Values" },
          { t: "p", s: "Compares two values (variables, strings, numbers) using an operator. Unlike element assertions, this works on variable values — not on screen elements." },
          { t: "code", lang: "text", s: "// Check login error message\nLeft: ${loginError}\nOperator: contains\nRight: Invalid\nFail if false: ON\n\n// Check price is less than 100\nLeft: ${price}\nOperator: <\nRight: 100\nParse as number: ON\n\n// Check username trimmed matches expected\nLeft: ${username}\nOperator: ==\nRight: admin@example.com\nTrim: ON\nIgnore case: ON" },
          { t: "tip", s: "Use Compare Values after Get Attribute or Set Variable (capture) to assert a captured value matches your expectation." },
        ],
      },
      {
        id: "cmd-variables", title: "Variables — Set, Get, Random, Get Attribute", tags: ["variable","set","get","random","attribute","capture"],
        blocks: [
          { t: "h3", s: "Set Variable" },
          { t: "p", s: "Stores a value in a named variable. Reference it in later steps as ${variableName}." },
          { t: "code", lang: "text", s: "// Set a fixed value\nName: username\nSource: Fixed value\nValue: admin@example.com\n\n// Capture text from screen element\nName: orderTotal\nSource: Capture from screen\nElement: by text 'Total: '\n→ orderTotal = 'Total: $42.50'\n\n// Store result of Random Value\nName: tempEmail\nSource: Random alphanumeric, 8 chars\n→ tempEmail = 'aB3xZ9qR'" },
          { t: "h3", s: "Get Variable" },
          { t: "p", s: "Prints the current value of a variable to the run log and report. Use for debugging." },
          { t: "code", lang: "text", s: "Variable name: orderTotal\n→ Prints: 'orderTotal = Total: $42.50' in the report" },
          { t: "h3", s: "Random Value" },
          { t: "p", s: "Generates a random value and stores it in a variable. Three types:" },
          { t: "table",
            headers: ["Type", "Example output", "Config"],
            rows: [
              ["Alphanumeric", "aB3xZ9qR", "Length (default 8)"],
              ["Letters only", "qxkrtzwm", "Length (default 8)"],
              ["Number", "4821", "Min and Max range"],
            ],
          },
          { t: "code", lang: "text", s: "// Generate a random 10-char username\nStore in: testUser\nType: Alphanumeric, length 10\n→ testUser = 'xQ2aB9mNpZ'\n\n// Generate a random order ID between 1000 and 9999\nStore in: orderId\nType: Number, min 1000, max 9999\n→ orderId = '4271'" },
          { t: "h3", s: "Get Attribute" },
          { t: "p", s: "Reads a specific accessibility attribute from an element and stores it as a variable." },
          { t: "code", lang: "text", s: "// Check if a button is enabled\nElement: Submit button (by a11yId)\nAttribute: enabled\nStore in: submitEnabled\n→ submitEnabled = 'true'\n\n// Get the width of an element\nAttribute: boundsWidth\nStore in: btnWidth\n→ btnWidth = '540'" },
          { t: "table",
            headers: ["Attribute", "Returns"],
            rows: [
              ["text", "Visible text of the element"],
              ["resourceId", "android:resource-id (e.g. com.app:id/btn)"],
              ["contentDesc", "Accessibility label"],
              ["className", "Android widget class (e.g. android.widget.Button)"],
              ["enabled", "'true' or 'false'"],
              ["checked", "'true' or 'false'"],
              ["clickable", "'true' or 'false'"],
              ["focused", "'true' or 'false'"],
              ["boundsWidth / boundsHeight", "Element size in device pixels"],
              ["boundsLeft / boundsTop", "Element position in device pixels"],
            ],
          },
          { t: "warn", s: "Visual attributes like color, font-size, and background are NOT available from the Android accessibility tree. Use Inspect Mode → CSS (available in Phase 5 for web testing) for those." },
        ],
      },
      {
        id: "cmd-string-functions", title: "String functions in ${...} expressions", tags: ["string","function","trim","upper","lower","substr","replace","concat"],
        blocks: [
          { t: "p", s: "Inside any string field that supports ${...} interpolation, you can call built-in string functions:" },
          { t: "table",
            headers: ["Function", "Example", "Result"],
            rows: [
              ["trim(x)", "${trim(myVar)}", "Strips leading/trailing whitespace"],
              ["upper(x)", "${upper(name)}", "UPPERCASE"],
              ["lower(x)", "${lower(email)}", "lowercase"],
              ["length(x)", "${length(text)}", "Character count as string"],
              ["substr(x,start,end)", "${substr(code,0,4)}", "First 4 characters"],
              ["replace(x,old,new)", "${replace(url,'http','https')}", "Replace all occurrences"],
              ["concat(a,b,...)", "${concat(firstName,' ',lastName)}", "Join strings"],
              ["number(x)", "${number(priceStr)}", "Parse float, return as string"],
              ["abs(x)", "${abs(diff)}", "Absolute value"],
              ["round(x)", "${round(score)}", "Round to integer"],
            ],
          },
          { t: "code", lang: "text", s: "// In a Type Text step:\nText: Hello ${upper(username)}!\n→ Types: Hello ADMIN!\n\n// In a Compare Values step:\nLeft: ${trim(capturedText)}\nOperator: ==\nRight: expected value\n\n// In a Print Log step:\nMessage: Full name: ${concat(firstName, ' ', lastName)}" },
        ],
      },
      {
        id: "cmd-control-flow", title: "Control Flow — If, Repeat, For Each Row, Break, Continue", tags: ["if","repeat","loop","foreach","break","continue","condition","flow"],
        blocks: [
          { t: "h3", s: "If Condition" },
          { t: "p", s: "Runs different steps depending on a condition. Supports two condition types:" },
          { t: "code", lang: "text", s: "// Element state condition:\nCheck: element state\nElement: Error message (by text)\nCondition: is visible\nThen: [tap Retry button]\nElse: [tap Continue button]\n\n// Value comparison condition:\nCheck: compare values\nLeft: ${loginStatus}\nOperator: ==\nRight: success\nThen: [navigate to dashboard]\nElse: [show error message step]" },
          { t: "h3", s: "Repeat" },
          { t: "p", s: "Runs a group of inner steps a fixed number of times." },
          { t: "code", lang: "text", s: "Repeat count: 5\nSteps inside:\n  → Swipe Up\n  → Wait 500ms\n\n// Swipes up 5 times to load more items" },
          { t: "h3", s: "For Each Row" },
          { t: "p", s: "Iterates over rows in a data source (Excel/CSV/JSON). The row variable holds the current row's data." },
          { t: "code", lang: "text", s: "Data source: users\nRow variable: user\nSteps inside:\n  → Open App\n  → Type Text: ${user.email}\n  → Type Text: ${user.password}\n  → Tap: Login button" },
          { t: "h3", s: "Break & Continue" },
          { t: "p", s: "Use inside Repeat or For Each Row loops only." },
          { t: "table",
            headers: ["Command", "Effect"],
            rows: [
              ["Break", "Exits the loop immediately — no more iterations"],
              ["Continue", "Skips the rest of the current iteration — moves to the next row/count"],
            ],
          },
        ],
      },
      {
        id: "cmd-compare", title: "Compare Values — all operators explained", tags: ["compare","operator","==","!=",">","<","contains","regex","empty"],
        blocks: [
          { t: "table",
            headers: ["Operator", "True when", "Example"],
            rows: [
              ["==", "Left equals Right (exact match)", "${status} == success"],
              ["!=", "Left does not equal Right", "${error} != ''"],
              [">", "Left is greater than Right (numeric)", "${count} > 0"],
              ["<", "Left is less than Right (numeric)", "${price} < 100"],
              [">=", "Greater than or equal (numeric)", "${score} >= 80"],
              ["<=", "Less than or equal (numeric)", "${age} <= 120"],
              ["contains", "Left string contains Right as substring", "${message} contains Error"],
              ["notContains", "Left string does NOT contain Right", "${page} notContains 404"],
              ["startsWith", "Left begins with Right", "${url} startsWith https://"],
              ["endsWith", "Left ends with Right", "${email} endsWith @company.com"],
              ["matches", "Left matches Right as a regex", "${phone} matches ^\\d{10}$"],
              ["isEmpty", "Left (after optional trim) is empty string", "${inputField} isEmpty"],
              ["isNotEmpty", "Left (after optional trim) is not empty", "${username} isNotEmpty"],
            ],
          },
          { t: "h3", s: "Options" },
          { t: "list", items: [
            "Trim: strips whitespace from both sides before comparing",
            "Ignore case: comparison is case-insensitive (== 'Hello' matches 'hello')",
            "Parse as number: parses both sides as floats before >, <, >=, <= comparisons",
          ]},
          { t: "code", lang: "text", s: "// Example: check a price field\nLeft: ${capturedPrice}\nOperator: <\nRight: 50\nParse as number: ON\nFail if false: ON\n→ Fails the test if capturedPrice >= 50" },
        ],
      },
      {
        id: "cmd-api", title: "API Call — making HTTP requests", tags: ["api","http","get","post","request","response"],
        blocks: [
          { t: "p", s: "The API Call step makes an HTTP request from the Studio browser. Use for setup (creating test data), teardown, or asserting backend state." },
          { t: "code", lang: "text", s: "// GET request\nMethod: GET\nURL: https://api.example.com/users/me\nStore response as: userData\n→ userData = {\"id\":1,\"name\":\"Alice\"}\n\n// POST request\nMethod: POST\nURL: https://api.example.com/login\nHeaders: {\"Content-Type\": \"application/json\"}\nBody: {\"email\":\"test@example.com\",\"password\":\"abc123\"}\nAssert status: 200\nStore response as: loginResponse\n\n// Use response in next step:\nType Text: ${loginResponse.token}" },
          { t: "warn", s: "API Call runs from the BROWSER, not the device. It is subject to browser CORS restrictions — the API server must allow cross-origin requests from localhost:5173. For CLI runs, API Call uses Node.js fetch with no CORS restriction." },
          { t: "tip", s: "Use API Call before a test to create a fresh test user, and after the test to clean up. This makes tests independent of each other." },
        ],
      },
      {
        id: "cmd-script", title: "Script — running JavaScript in a sandbox", tags: ["script","javascript","code","sandbox","custom logic"],
        blocks: [
          { t: "p", s: "The Script step runs a JavaScript snippet in a secure sandbox. Use it for computation, string manipulation, or conditional logic that other commands cannot express." },
          { t: "code", lang: "javascript", s: "// Access current variables\nconst { vars } = context;\n\n// String manipulation\nconst cleaned = vars.rawName.trim().toLowerCase();\nconst initials = cleaned.split(' ').map(w => w[0]).join('');\n\n// Math\nconst tax = parseFloat(vars.price) * 0.18;\nconst total = parseFloat(vars.price) + tax;\n\n// Return: update variables\nreturn {\n  vars: {\n    cleanName: cleaned,\n    initials: initials,\n    totalWithTax: total.toFixed(2),\n  },\n  // Optional: fail the step\n  // fail: 'reason string if you want to fail',\n};" },
          { t: "h3", s: "Security" },
          { t: "list", items: [
            "Script runs in an isolated iframe sandbox — it CANNOT access the Studio's DOM, localStorage, or cookies",
            "No network access (no fetch/XHR) — use the API Call step for HTTP",
            "No ADB access — scripts manipulate variables only",
            "5-second timeout — scripts that hang are killed automatically",
          ]},
          { t: "warn", s: "Script is for computation only. Do NOT use it to attempt DOM access, file system access, or any system calls — they will throw permission errors in the sandbox." },
        ],
      },
      {
        id: "cmd-device", title: "Device commands — WiFi, Airplane, Dark Mode, Location", tags: ["wifi","airplane","dark mode","location","keyboard","device"],
        blocks: [
          { t: "table",
            headers: ["Command", "Effect", "Notes"],
            rows: [
              ["WiFi On", "Enables WiFi radio", "Avoid if connected via WiFi ADB"],
              ["WiFi Off", "Disables WiFi radio", "DANGER: kills WiFi ADB session"],
              ["Airplane On", "Enables airplane mode", "DANGER: kills all connectivity including WiFi ADB"],
              ["Airplane Off", "Disables airplane mode", ""],
              ["Dark Mode On", "Switches to dark theme (Android 10+)", "Persists after test — remember to reset"],
              ["Dark Mode Off", "Switches to light theme", ""],
              ["Location On", "Enables location services (high accuracy)", "GPS + network mode"],
              ["Location Off", "Disables location services", ""],
              ["Show Keyboard", "Shows the soft keyboard (IME)", "Requires a text field to be focused"],
              ["Hide Keyboard", "Dismisses the soft keyboard", ""],
            ],
          },
          { t: "warn", s: "WiFi Off and Airplane On will disconnect your USB-over-WiFi ADB session. Only use these if connected via physical USB cable." },
        ],
      },
    ],
  },

  // ── DATA SOURCES ─────────────────────────────────────────────────────────
  {
    id: "data", label: "Variables & Data Sources", icon: "📊",
    articles: [
      {
        id: "variables-guide", title: "Variables — how they work", tags: ["variable","scope","interpolation","runtime"],
        blocks: [
          { t: "p", s: "Variables are key-value pairs that live for the duration of one test run. They are created by Set Variable, Get Attribute, Random Value, and API Call (storeResponseAs). They are consumed using ${varName} syntax in any text field." },
          { t: "h3", s: "Setting a variable" },
          { t: "code", lang: "text", s: "Set Variable:\n  Name: myVar\n  Source: Fixed value\n  Value: hello\n\n// Later in the test:\nType Text: ${myVar}   → Types: hello\nPrint Log: ${myVar}   → Logs: hello" },
          { t: "h3", s: "Variable scope" },
          { t: "list", items: [
            "Variables exist for the entire run — once set, they are available to all subsequent steps",
            "Variables do NOT persist between runs — each run starts with an empty variable context",
            "Environment variables (${env:KEY}) come from the project config or CLI --env flags — they are available at the start of every run",
          ]},
          { t: "h3", s: "Nested variables" },
          { t: "code", lang: "text", s: "// If API response is stored as 'loginResult':\nStore response as: loginResult\n\n// Access nested fields using dot notation:\nType Text: ${loginResult.token}\nPrint Log: User: ${loginResult.user.name}" },
        ],
      },
      {
        id: "excel-data", title: "Using Excel / CSV data in tests", tags: ["excel","csv","data","foreach","row","datasource"],
        blocks: [
          { t: "p", s: "The For Each Row step iterates over tabular data from an Excel (.xlsx), CSV, or JSON file. This enables data-driven testing — run the same test steps with different input values." },
          { t: "h3", s: "Preparing your data file" },
          { t: "code", lang: "text", s: "users.csv:\nemail,password,expectedName\ntest1@example.com,pass123,Alice\ntest2@example.com,secure99,Bob\nadmin@example.com,admin!,Admin User" },
          { t: "h3", s: "Configuring the data source" },
          { t: "steps", items: [
            "Go to Settings → Project Config → Data Files Path (default: ./test-data)",
            "Save your .csv or .xlsx file in that directory",
            "Name the file exactly as you will reference it (e.g. users.csv → data source name: users)",
          ]},
          { t: "h3", s: "Using For Each Row in a test" },
          { t: "code", lang: "text", s: "For Each Row:\n  Data source: users\n  Row variable: u\n  Steps:\n    → Open App: com.example.app\n    → Tap: Email field\n    → Type Text: ${u.email}\n    → Tap: Password field\n    → Type Text: ${u.password}\n    → Tap: Login button\n    → Verify Visible: ${u.expectedName}" },
          { t: "tip", s: "Column headers become the field names. Spaces in headers are replaced with underscores: 'Expected Name' → ${u.Expected_Name}." },
          { t: "h3", s: "Running with CLI" },
          { t: "code", lang: "bash", s: "mq run tests/login.json --device 192.168.1.42:5555 --data-dir ./test-data" },
        ],
      },
      {
        id: "json-data", title: "Using JSON data files", tags: ["json","data","datasource","foreach"],
        blocks: [
          { t: "p", s: "JSON data sources work the same way as CSV — place a .json file in your data directory and reference it by name (without extension)." },
          { t: "code", lang: "json", s: "// test-data/products.json\n[\n  { \"id\": \"P001\", \"name\": \"Laptop\", \"price\": 999 },\n  { \"id\": \"P002\", \"name\": \"Phone\",  \"price\": 599 },\n  { \"id\": \"P003\", \"name\": \"Tablet\", \"price\": 349 }\n]" },
          { t: "code", lang: "text", s: "For Each Row:\n  Data source: products\n  Row variable: p\n  Steps:\n    → Type Text: ${p.name}\n    → Verify Visible: ${p.price}\n    → Tap: Add to Cart" },
          { t: "tip", s: "JSON data can have nested objects. Access them with dot notation: ${p.address.city}. Arrays within rows are not iterable in steps — flatten your data structure." },
        ],
      },
      {
        id: "env-vars", title: "Environment variables", tags: ["environment","env","config","secret","key"],
        blocks: [
          { t: "p", s: "Environment variables are values set outside the test file — in the project config or on the CLI. They are available in any step as ${env:KEY}." },
          { t: "h3", s: "Setting env vars in Studio (Settings)" },
          { t: "code", lang: "json", s: "// Settings → Project Config → Extra Env Vars (JSON)\n{\n  \"API_URL\": \"https://staging.example.com\",\n  \"TEST_USER\": \"qa@example.com\",\n  \"TIMEOUT_MS\": \"15000\"\n}" },
          { t: "h3", s: "Using env vars in steps" },
          { t: "code", lang: "text", s: "API Call:\n  URL: ${env:API_URL}/users\n\nType Text: ${env:TEST_USER}" },
          { t: "h3", s: "Setting env vars in CLI" },
          { t: "code", lang: "bash", s: "mq run tests/login.json \\\n  --env API_URL=https://prod.example.com \\\n  --env TEST_USER=prod@example.com" },
          { t: "warn", s: "Never store passwords or secrets in the IR file directly. Always use ${env:SECRET_KEY} and pass the value via CLI or vault integration. The env: prefix ensures the secret is never serialized to disk by the Studio." },
        ],
      },
    ],
  },

  // ── REPORTS ──────────────────────────────────────────────────────────────
  {
    id: "reports", label: "Reports & Export", icon: "📈",
    articles: [
      {
        id: "reports-overview", title: "Viewing test reports", tags: ["report","result","pass","fail","status"],
        blocks: [
          { t: "p", s: "The Reports tab shows all past test runs across all tests. Runs are sorted newest first." },
          { t: "h3", s: "Run status badges" },
          { t: "table",
            headers: ["Badge", "Meaning"],
            rows: [
              ["✓ Passed", "All steps succeeded"],
              ["✗ Failed", "One or more steps failed (step detail shows which)"],
              ["⚕ Healed", "All steps passed but some were auto-healed by the AI"],
              ["! Error", "Run terminated due to an unexpected error"],
            ],
          },
          { t: "h3", s: "Expanding a run" },
          { t: "p", s: "Click any run row to expand per-step detail: step type, step name, status, matched-by strategy, coordinates, and detail/hint." },
          { t: "h3", s: "Screenshot column" },
          { t: "p", s: "If screenshots were captured (screenshot step or Settings → Screenshot Every Step), thumbnails appear in the report. Click any thumbnail to open full-screen." },
          { t: "h3", s: "Accessibility Tree log (on failure)" },
          { t: "p", s: "If Settings → Capture Logs on Failure is ON, failed steps show a 📋 Accessibility Tree section. This contains the full JSON tree from the moment the step failed — invaluable for debugging why a selector did not match." },
        ],
      },
      {
        id: "export-reports", title: "Exporting reports as HTML", tags: ["export","html","download","report"],
        blocks: [
          { t: "p", s: "Every test run can be exported as a self-contained HTML file that can be opened in any browser, emailed, or archived." },
          { t: "h3", s: "Export a single run" },
          { t: "steps", items: [
            "Go to the Reports tab",
            "Find the run in the list",
            "Click the ↓ HTML button on the right side of the row",
            "The file downloads immediately",
          ]},
          { t: "h3", s: "Export multiple runs as ZIP" },
          { t: "steps", items: [
            "Use the filters (search, status, date range) to narrow the list",
            "Click ↓ Export Reports (N) at the top right",
            "A ZIP file downloads containing one HTML per run",
          ]},
          { t: "warn", s: "If Screenshot Every Step was ON, each HTML report can be 50–150 MB. Share the ZIP via a file sharing service rather than email. Screenshots are embedded as base64 inside the HTML." },
          { t: "h3", s: "What the exported HTML contains" },
          { t: "list", items: [
            "Test name, device model, Android version, run date, duration",
            "Pass/Fail/Total summary cards",
            "Per-step table: #, Type, Step Name, Status, Detail, Screenshot",
            "Accessibility tree log on failed steps (collapsible)",
            "All CSS and images inline — no external dependencies",
          ]},
        ],
      },
    ],
  },

  // ── FUNCTION LIBRARY ──────────────────────────────────────────────────────
  {
    id: "functions", label: "Function Library", icon: "♻",
    articles: [
      {
        id: "fn-overview", title: "What are Functions?", tags: ["function","library","reusable","keyword"],
        blocks: [
          { t: "p", s: "A Function is a named, reusable sequence of steps with optional input parameters. Think of it as a \"step macro\" — record the steps once, call them from any test as many times as you need." },
          { t: "p", s: "Example: your Login flow is used in 30 tests. Without functions, every test has 8 login steps repeated. With functions, you create a 'Login' function once and call it with `Call Function` in each test." },
          { t: "h3", s: "Benefits" },
          { t: "list", items: [
            "Change the login flow once → all 30 tests pick it up automatically",
            "Tests are shorter and easier to read — one step instead of 8",
            "Parameters let the same function work with different users/data",
            "Export and share functions across projects (ZIP import/export)",
          ]},
          { t: "tip", s: "Functions are similar to Robot Framework Keywords or Cypress custom commands — but completely no-code, managed in the browser." },
        ],
      },
      {
        id: "fn-create", title: "Creating a Function", tags: ["create","function","new","steps"],
        blocks: [
          { t: "steps", items: [
            "Click ♻ Functions in the left sidebar",
            "Click + New Function",
            "Enter a name (e.g. 'Login as Admin') and an optional description",
            "Optionally assign it to a folder (create one first with + Folder)",
            "Click Create — the function appears in the list with 0 steps",
            "Click the function in the list to open its detail panel",
            "Click ✎ Edit Steps in Recorder — the Recorder opens with a purple banner: 'Editing function: Login as Admin'",
            "Record or manually add steps exactly as you would for a test",
            "Click ✓ Save to Function in the purple banner — steps are saved back to the function",
          ]},
          { t: "warn", s: "When editing function steps, use the purple 'Save to Function' button — NOT the normal 'Save As' button. The normal Save would create a TestCase, not update the function." },
        ],
      },
      {
        id: "fn-parameters", title: "Function Parameters", tags: ["parameter","argument","input","variable"],
        blocks: [
          { t: "p", s: "Parameters are named inputs that callers pass each time they call the function. Inside function steps, reference them as ${paramName}." },
          { t: "h3", s: "Adding a parameter" },
          { t: "steps", items: [
            "Open the function detail panel",
            "Click + Add in the Parameters section",
            "Enter: Name (e.g. username), Description (e.g. 'The account email'), Default value (optional, e.g. admin@example.com)",
            "Click Add",
            "The parameter is now available as ${username} inside all steps of this function",
          ]},
          { t: "h3", s: "Using parameters inside steps" },
          { t: "code", lang: "text", s: "// Example: 'Login as Admin' function with 2 parameters\nParameters:\n  username  — default: admin@example.com\n  password  — default: (empty)\n\nSteps inside the function:\n  1. Open App: com.example.app\n  2. Tap: Email field\n  3. Type Text: ${username}      ← uses the parameter\n  4. Tap: Password field\n  5. Type Text: ${password}      ← uses the parameter\n  6. Tap: Login button" },
          { t: "tip", s: "If a parameter has a default value, callers can omit it. If it has no default, callers MUST provide a value or the step will receive an empty string." },
        ],
      },
      {
        id: "fn-call", title: "Calling a Function from a Test", tags: ["call","callflow","use","invoke"],
        blocks: [
          { t: "p", s: "To use a function inside a test, add a Call Function step." },
          { t: "h3", s: "Method 1 — from the Command Toolbar" },
          { t: "steps", items: [
            "In the Recorder, open the Flow category chip",
            "Click ♻ Call Function (N)",
            "A searchable picker appears — type to filter functions",
            "Select your function",
            "If the function has parameters, fill in the argument values",
            "Click Add",
          ]},
          { t: "h3", s: "Method 2 — from + Add Step" },
          { t: "steps", items: [
            "Click + Add Step at the bottom of the step list",
            "Type 'call' or 'function' in the search box",
            "Select 'Call Function'",
            "Type to search functions, select one",
            "Fill in any parameter values",
            "Click Add Step",
          ]},
          { t: "h3", s: "Example — calling 'Login as Admin'" },
          { t: "code", lang: "text", s: "// In your test steps:\n1. Call Function: Login as Admin\n   username: qa_user@example.com\n   password: TestPass99!\n\n// This is equivalent to:\n1. Open App: com.example.app\n2. Tap: Email field\n3. Type Text: qa_user@example.com\n4. Tap: Password field\n5. Type Text: TestPass99!\n6. Tap: Login button" },
          { t: "tip", s: "The function's steps execute at run time as if they were inline in your test. Variables set inside the function (with Set Variable) are accessible to steps after the Call Function step." },
        ],
      },
      {
        id: "fn-folders", title: "Organizing Functions into Folders", tags: ["folder","organize","hierarchy","subfolder"],
        blocks: [
          { t: "p", s: "Functions can be organized into nested folders — useful when you have many functions for different app areas." },
          { t: "steps", items: [
            "Click + Folder in the Functions header",
            "Enter a folder name (e.g. 'Authentication', 'Checkout')",
            "Optionally choose a parent folder for nesting",
            "Click Create",
            "To move a function into a folder: open its detail panel → click the Folder dropdown → select the folder",
            "To delete a folder: click ✕ on the folder header — you choose whether to delete its functions or move them to root",
          ]},
          { t: "code", lang: "text", s: "Example folder structure:\n📁 Authentication\n  ♻ Login as Admin\n  ♻ Login as Guest\n  ♻ Logout\n📁 Checkout\n  ♻ Add Item to Cart\n  ♻ Enter Shipping Address\n  ♻ Complete Payment\n♻ Verify App Launches   ← root (no folder)" },
        ],
      },
      {
        id: "fn-export", title: "Exporting and Importing Functions", tags: ["export","import","share","zip","json"],
        blocks: [
          { t: "p", s: "Functions can be exported as .json (single) or .zip (multiple + folders) and imported into any Mimiq Studio instance." },
          { t: "h3", s: "Export options" },
          { t: "table",
            headers: ["Where", "What", "Output"],
            rows: [
              ["List item → ↓ button", "Single function", "functionname.mimiq-fn.json"],
              ["Detail panel → ↓ Download", "Single function", "functionname.mimiq-fn.json"],
              ["Folder header → ↓ button", "All functions in folder + subfolders", "foldername-functions.zip"],
              ["Header → ↓ Export All", "Every function in the library", "mimiq-functions-date.zip"],
            ],
          },
          { t: "h3", s: "Import" },
          { t: "steps", items: [
            "Click ↑ Import in the Functions header",
            "Select a .json file (single function) or .zip file (multiple)",
            "For ZIP: the folder structure is recreated automatically from the manifest",
            "A toast shows how many functions were imported",
          ]},
          { t: "tip", s: "When you export a test that uses Call Function steps, the functions are NOT automatically included. Export the functions separately using Export All." },
        ],
      },
    ],
  },

  // ── CLI ───────────────────────────────────────────────────────────────────
  {
    id: "cli", label: "CLI Reference", icon: "⌨️",
    articles: [
      {
        id: "cli-install", title: "Installing the CLI (mq)", tags: ["install","cli","mq","npm","pnpm"],
        blocks: [
          { t: "code", lang: "bash", s: "# Install globally from npm\nnpm install -g @mimiq/cli\n\n# Or use directly with npx\nnpx @mimiq/cli run tests/login.json --device <serial>" },
          { t: "tip", s: "Verify install: mq --version should print the CLI version number." },
        ],
      },
      {
        id: "cli-run", title: "mq run — executing a test", tags: ["run","cli","execute","device","serial"],
        blocks: [
          { t: "code", lang: "bash", s: "# Basic run against a USB device\nmq run tests/login.json --device <serial>\n\n# Get your device serial:\nadb devices\n# > List of devices attached\n# > R9CTB12345   device\n\n# Run against an emulator\nmq run tests/login.json --device emulator-5554\n\n# WiFi ADB (connect first)\nadb tcpip 5555\nadb connect 192.168.1.42:5555\nmq run tests/login.json --device 192.168.1.42:5555" },
          { t: "h3", s: "All mq run flags" },
          { t: "table",
            headers: ["Flag", "Default", "Description"],
            rows: [
              ["--device <serial>", "Required", "ADB device serial (from adb devices)"],
              ["--step-delay <ms>", "800", "Pause between steps in milliseconds"],
              ["--timeout <ms>", "15000", "Per-step element wait timeout"],
              ["--data-dir <path>", "./test-data", "Directory for Excel/CSV/JSON data sources"],
              ["--env KEY=VALUE", "—", "Set environment variable (repeatable)"],
              ["--config <file>", "mimiq.config.json", "Path to config file"],
              ["--output <dir>", "./mimiq-results", "Directory for report output"],
              ["--heal", "off", "Enable Tier-1 ONNX AI healing on selector failures"],
              ["--parallel <n>", "1", "Run N tests in parallel (cloud only)"],
              ["--platform <p>", "android", "android | ios | web"],
            ],
          },
          { t: "code", lang: "bash", s: "# Full example with all common flags\nmq run tests/login.json \\\n  --device R9CTB12345 \\\n  --step-delay 500 \\\n  --timeout 20000 \\\n  --env API_URL=https://staging.myapp.com \\\n  --env TEST_USER=qa@myapp.com \\\n  --data-dir ./test-data \\\n  --output ./reports \\\n  --heal" },
        ],
      },
      {
        id: "cli-heal", title: "mq heal — AI-powered selector repair", tags: ["heal","ai","locator","fix","onnx"],
        blocks: [
          { t: "p", s: "When a test fails because an element is not found, mq heal can suggest (or automatically apply) a fixed locator based on the AI fingerprint recorded at record time." },
          { t: "h3", s: "How healing works" },
          { t: "list", items: [
            "Tier 0: Exact candidate match — no healing needed",
            "Tier 1 (ONNX, local): Fuzzy match using semantic embeddings — runs in <400ms on CPU",
            "Tier 2 (Ollama, optional): Local VLM suggests fixes — authoring only, never runs steps",
            "Tier 3 (Remote LLM, opt-in): Cloud AI for complex cases — requires explicit opt-in",
          ]},
          { t: "code", lang: "bash", s: "# Run with healing enabled\nmq run tests/login.json --device R9CTB12345 --heal\n\n# Review suggested heals\nmq heal review\n\n# Commit heals (prepends fixed candidate to the step's candidate list)\nmq heal commit\n\n# Commit a specific heal\nmq heal commit --step-id 01H5X..." },
          { t: "warn", s: "Auto-healed runs are ALWAYS marked 'passed (healed)' in reports — never silently green. This is intentional so you know healing occurred and can review the new candidate." },
        ],
      },
      {
        id: "cli-config", title: "mimiq.config.json — project configuration", tags: ["config","configuration","json","file","project"],
        blocks: [
          { t: "p", s: "The config file lives at the root of your project. Settings from Studio (Settings → Project Config → Download mimiq.config.json) match this format." },
          { t: "code", lang: "json", s: "{\n  \"project\": \"My App Tests\",\n  \"app\": {\n    \"packageName\": \"com.example.myapp\"\n  },\n  \"env\": {\n    \"API_URL\": \"https://staging.myapp.com\",\n    \"TEST_USER\": \"qa@myapp.com\"\n  },\n  \"timeouts\": {\n    \"stepMs\": 15000\n  },\n  \"reporting\": {\n    \"screenshotOnFailure\": true\n  },\n  \"dataSources\": {\n    \"basePath\": \"./test-data\"\n  },\n  \"healing\": {\n    \"maxTierUsed\": 1,\n    \"minConfidence\": 0.75,\n    \"mode\": \"suggest\"\n  }\n}" },
          { t: "h3", s: "Config field reference" },
          { t: "table",
            headers: ["Field", "Type", "Description"],
            rows: [
              ["project", "string", "Project display name"],
              ["app.packageName", "string", "Default app to test (used by Open App)"],
              ["env", "object", "Environment variables available as ${env:KEY}"],
              ["timeouts.stepMs", "number", "Per-step element wait timeout in ms (default 15000)"],
              ["reporting.screenshotOnFailure", "boolean", "Capture screenshot on every failed step"],
              ["dataSources.basePath", "string", "Directory for Excel/CSV/JSON data files"],
              ["healing.maxTierUsed", "0|1|2|3", "Maximum AI tier to use (0=none, 1=ONNX, 2=Ollama, 3=remote)"],
              ["healing.minConfidence", "0..1", "Minimum confidence score for AI to accept a match"],
              ["healing.mode", "suggest|auto", "suggest=report only, auto=apply and continue"],
            ],
          },
        ],
      },
      {
        id: "cli-step-timeout", title: "Step timeout — tuning element wait", tags: ["timeout","wait","slow","element","not found"],
        blocks: [
          { t: "p", s: "Each step waits up to the step timeout for an element to appear. The default is 15 seconds. The runner polls the accessibility tree until the element is found or the timeout expires." },
          { t: "code", lang: "bash", s: "# Set a custom timeout (30 seconds) for slow loading screens\nmq run tests/checkout.json --device R9CTB12345 --timeout 30000\n\n# In config file:\n{\n  \"timeouts\": { \"stepMs\": 30000 }\n}" },
          { t: "tip", s: "If your test hits step timeouts on a fast machine, the element might be there but not yet interactive. Try adding a Wait For Element step before the tap instead of increasing the global timeout." },
          { t: "h3", s: "Per-step timeout (via waitFor)" },
          { t: "code", lang: "text", s: "Wait For Element:\n  Element: Loading spinner (by contentDesc)\n  Wait for: hidden\n  Timeout: 30000    → wait up to 30s for spinner to disappear" },
        ],
      },
      {
        id: "cli-logs", title: "Extracting logs from failed runs", tags: ["logs","a11y","accessibility","debug","failure","tree"],
        blocks: [
          { t: "p", s: "When a step fails, Mimiq can capture the accessibility tree at the moment of failure. This snapshot shows exactly what was on screen and why the selector did not match." },
          { t: "h3", s: "Enable log capture in Studio" },
          { t: "steps", items: [
            "Go to Settings → Project Config",
            "Enable Capture Logs on Failure checkbox",
            "Save to Session",
            "Run your test from the Execution tab",
            "When a step fails, expand it in Reports → click 📋 Accessibility Tree",
            "Download the JSON for offline analysis with ↓ JSON button",
          ]},
          { t: "h3", s: "Interpreting the accessibility tree" },
          { t: "code", lang: "json", s: "// Example: looking for a Login button that failed\n[\n  {\n    \"text\": \"Sign In\",           // ← button text changed from Login!\n    \"resourceId\": \"com.app:id/btn_login\",\n    \"className\": \"android.widget.Button\",\n    \"enabled\": true,\n    \"clickable\": true,\n    \"bounds\": { \"left\": 200, \"top\": 800, \"right\": 880, \"bottom\": 880 }\n  }\n]\n// Fix: update the step's 'text' candidate from 'Login' to 'Sign In'" },
          { t: "h3", s: "CLI log output" },
          { t: "code", lang: "bash", s: "# Logs are written to stdout by default\nmq run tests/login.json --device R9CTB12345\n\n# Redirect to file\nmq run tests/login.json --device R9CTB12345 > run.log 2>&1\n\n# View live with tee\nmq run tests/login.json --device R9CTB12345 | tee run.log" },
        ],
      },
    ],
  },

  // ── SUPPORT & DONATE ──────────────────────────────────────────────────────
  {
    id: "support", label: "Support & Donate", icon: "💝",
    articles: [
      {
        id: "donate", title: "Support Mimiq's development", tags: ["donate","support","coffee","fund"],
        blocks: [
          { t: "p", s: "Mimiq is free to use on your own Android phone — no account, no cloud bill, no expiry. We are an independent team building the best mobile test automation tool on the planet." },
          { t: "h3", s: "What your support funds" },
          { t: "list", items: [
            "Cloud device pools (Path B) — run tests on devices you don't own",
            "iOS Simulator support (Phase 5)",
            "AI healing improvements — better ONNX models",
            "The Wiki you are reading right now",
            "Zero-installation improvements and bug fixes",
          ]},
          { t: "info", s: "Every coffee ☕ buys ~1 hour of dev time. Enough for one new feature, one bug fix, or one performance improvement." },
          { t: "h3", s: "How to donate" },
          { t: "steps", items: [
            "Go to Settings → Help & Community",
            "Click the Donate ♥ button",
            "Choose your preferred amount on Ko-fi",
            "You're fuelling the next version of Mimiq — thank you! 🙏",
          ]},
          { t: "h3", s: "Other ways to help" },
          { t: "list", items: [
            "⭐ Star the GitHub repository",
            "Report bugs via Settings → Contact Support",
            "Share Mimiq with your QA and dev colleagues",
            "Write about Mimiq — blog posts, tweets, LinkedIn posts all help tremendously",
          ]},
        ],
      },
      {
        id: "contact", title: "Reporting bugs & requesting features", tags: ["bug","feature","support","contact","email"],
        blocks: [
          { t: "p", s: "We read every support request personally. The fastest path to a fix is a clear description with a screenshot or recording." },
          { t: "steps", items: [
            "Go to Settings → Help & Community → Contact Support",
            "Select category: Bug Report, Feature Request, General Query",
            "Describe the issue — include steps to reproduce",
            "Attach screenshots or log files using the file attachment field",
            "Click Open Email Client — a pre-filled email opens in your mail app",
            "Attach any files manually before sending",
          ]},
          { t: "tip", s: "For fastest resolution: include your Android version, phone model, Chrome version, and the exact step that fails. A downloaded HTML report with the error is ideal." },
        ],
      },
    ],
  },
];

// ── Flat article list for search ──────────────────────────────────────────────

const ALL_ARTICLES: (Article & { catLabel: string })[] = WIKI.flatMap((cat) =>
  cat.articles.map((a) => ({ ...a, catLabel: cat.label }))
);

// ── Component ─────────────────────────────────────────────────────────────────

export function WikiPage() {
  const [query, setQuery]           = useState("");
  const [activeId, setActiveId]     = useState(WIKI[0]!.articles[0]!.id);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(
    () => new Set(WIKI.map((c) => c.id))
  );

  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    return ALL_ARTICLES.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.tags.some((t) => t.includes(q)) ||
        a.blocks.some(
          (b) =>
            ("s" in b && b.s.toLowerCase().includes(q)) ||
            ("items" in b && b.items.some((i) => i.toLowerCase().includes(q)))
        )
    );
  }, [query]);

  const activeArticle =
    ALL_ARTICLES.find((a) => a.id === activeId) ??
    ALL_ARTICLES[0]!;

  function toggleCat(id: string) {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openArticle(id: string) {
    setActiveId(id);
    setQuery("");
  }

  return (
    <div className="wiki-page">
      {/* ── Left nav ── */}
      <div className="wiki-nav">
        <div className="wiki-search-wrap">
          <input
            className="wiki-search"
            placeholder="🔍  Search docs…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="wiki-search-clear" onClick={() => setQuery("")}>✕</button>
          )}
        </div>

        {searchResults ? (
          <ul className="wiki-search-results">
            {searchResults.length === 0 ? (
              <li className="wiki-search-empty">No results for "{query}"</li>
            ) : (
              searchResults.map((a) => (
                <li key={a.id}>
                  <button className="wiki-nav-item" onClick={() => openArticle(a.id)}>
                    <span className="wiki-nav-cat">{a.catLabel}</span>
                    {a.title}
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : (
          WIKI.map((cat) => (
            <div key={cat.id} className="wiki-cat">
              <button
                className="wiki-cat-header"
                onClick={() => toggleCat(cat.id)}
              >
                <span>{cat.icon} {cat.label}</span>
                <span className="wiki-cat-chevron">{expandedCats.has(cat.id) ? "▾" : "▸"}</span>
              </button>
              {expandedCats.has(cat.id) && (
                <ul className="wiki-cat-articles">
                  {cat.articles.map((a) => (
                    <li key={a.id}>
                      <button
                        className={`wiki-nav-item${activeId === a.id ? " wiki-nav-item--active" : ""}`}
                        onClick={() => openArticle(a.id)}
                      >
                        {a.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))
        )}
      </div>

      {/* ── Article content ── */}
      <div className="wiki-content">
        <div className="wiki-breadcrumb">
          {ALL_ARTICLES.find((a) => a.id === activeId)?.catLabel} › {activeArticle.title}
        </div>
        <h1 className="wiki-article-title">{activeArticle.title}</h1>
        <div className="wiki-article-body">
          {activeArticle.blocks.map((block, bi) => (
            <WikiBlock key={bi} block={block} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Block renderer ────────────────────────────────────────────────────────────

function WikiBlock({ block }: { block: Block }) {
  switch (block.t) {
    case "p":    return <p className="wiki-p">{block.s}</p>;
    case "h3":   return <h3 className="wiki-h3">{block.s}</h3>;
    case "h4":   return <h4 className="wiki-h4">{block.s}</h4>;
    case "tip":  return <div className="wiki-callout wiki-callout--tip">💡 {block.s}</div>;
    case "warn": return <div className="wiki-callout wiki-callout--warn">⚠️ {block.s}</div>;
    case "info": return <div className="wiki-callout wiki-callout--info">ℹ️ {block.s}</div>;
    case "code":
      return (
        <pre className="wiki-code">
          <code>{block.s}</code>
        </pre>
      );
    case "steps":
      return (
        <ol className="wiki-steps">
          {block.items.map((item, i) => <li key={i}>{item}</li>)}
        </ol>
      );
    case "list":
      return (
        <ul className="wiki-list">
          {block.items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      );
    case "table":
      return (
        <div className="wiki-table-wrap">
          <table className="wiki-table">
            <thead>
              <tr>{block.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}
